export interface PlanningActionRegistration {
  projectId: string;
  sessionId: string | null;
  entryId: string;
  buttonKey: string;
  label: string;
  enabled: boolean;
  current: boolean;
  invoke: () => void | Promise<void>;
}

export interface TypedPlanningActionRequest {
  text: string;
  projectId: string;
  sessionId: string | null;
  mediaUrls?: readonly string[] | null;
}

interface StoredPlanningAction extends PlanningActionRegistration {
  identity: string;
  normalizedLabel: string;
  token: number;
}

const registrations = new Map<number, StoredPlanningAction>();
const inFlightIdentities = new Set<string>();
let nextToken = 1;

export const normalizePlanningActionLabel = (value: string): string => (
  value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase()
);

const actionIdentity = (action: PlanningActionRegistration): string => JSON.stringify([
  action.projectId,
  action.sessionId,
  action.entryId,
  action.buttonKey,
]);

/**
 * Register one action that is currently available in the planning UI.
 *
 * Callers own the action's lifecycle: register from an effect and invoke the
 * returned cleanup when it becomes hidden, historical, disabled, or unmounted.
 * Duplicate responsive renderings are safe because invocation deduplicates by
 * project/session/entry/button identity.
 */
export const registerPlanningAction = (
  action: PlanningActionRegistration,
): (() => void) => {
  const normalizedLabel = normalizePlanningActionLabel(action.label);

  if (!action.projectId || !action.entryId || !action.buttonKey || !normalizedLabel) {
    return () => {};
  }

  const token = nextToken;

  nextToken += 1;
  registrations.set(token, {
    ...action,
    identity: actionIdentity(action),
    normalizedLabel,
    token,
  });

  return () => {
    registrations.delete(token);
  };
};

const matchingActions = (
  request: TypedPlanningActionRequest,
): Map<string, StoredPlanningAction[]> => {
  const normalizedText = normalizePlanningActionLabel(request.text);
  const matches = new Map<string, StoredPlanningAction[]>();

  if (!request.projectId || !normalizedText || (request.mediaUrls?.length ?? 0) > 0) return matches;

  registrations.forEach((action) => {
    if (
      !action.current
      || !action.enabled
      || action.projectId !== request.projectId
      || action.sessionId !== request.sessionId
      || action.normalizedLabel !== normalizedText
    ) return;

    const sameIdentity = matches.get(action.identity) ?? [];

    sameIdentity.push(action);
    matches.set(action.identity, sameIdentity);
  });

  return matches;
};

/**
 * Invoke a unique exact match and report whether the input was consumed.
 *
 * Ambiguous labels deliberately fall through to normal chat. A repeated send
 * while the same action is awaiting its handler is consumed without invoking
 * the handler twice.
 */
const uniqueMatchingAction = (
  request: TypedPlanningActionRequest,
): { action: StoredPlanningAction; identity: string } | null => {
  const matches = matchingActions(request);

  if (matches.size !== 1) return null;

  const [identity, duplicates] = Array.from(matches.entries())[0];
  // Prefer the latest mounted copy when responsive layouts temporarily render
  // the same logical action in two places.
  const action = duplicates.reduce((latest, candidate) => (
    candidate.token > latest.token ? candidate : latest
  ));

  return { action, identity };
};

const runPlanningAction = async (
  action: StoredPlanningAction,
  identity: string,
): Promise<void> => {
  inFlightIdentities.add(identity);
  try {
    await action.invoke();
  } finally {
    inFlightIdentities.delete(identity);
  }
};

export const invokeExactPlanningAction = async (
  request: TypedPlanningActionRequest,
): Promise<boolean> => {
  const match = uniqueMatchingAction(request);

  if (!match) return false;
  if (inFlightIdentities.has(match.identity)) return true;

  await runPlanningAction(match.action, match.identity);

  return true;
};

const currentActionWithLabel = (projectId: string, labels: readonly string[]): StoredPlanningAction | null => {
  const wanted = labels.map(normalizePlanningActionLabel).filter(Boolean);
  let found: StoredPlanningAction | null = null;

  registrations.forEach((action) => {
    const matches = action.current && action.enabled && action.projectId === projectId
      && wanted.some((label) => action.normalizedLabel.startsWith(label));

    if (matches && (!found || action.token > found.token)) found = action;
  });

  return found;
};

/**
 * Press the planning control on screen that runs a step another surface needs, e.g. the gate's
 * "Generate Task Plan" when Start Building lists the task plan first. Labels match as prefixes
 * ("Generate All Pages" matches "Generate All Pages (3 missing)"). False when none is current.
 */
export const invokeCurrentPlanningAction = async (
  projectId: string,
  labels: readonly string[],
): Promise<boolean> => {
  const action = currentActionWithLabel(projectId, labels);

  if (!action) return false;
  if (!inFlightIdentities.has(action.identity)) await runPlanningAction(action, action.identity);

  return true;
};

/** Test-only reset for this module-level UI registry. */
export const resetPlanningActionRegistry = (): void => {
  registrations.clear();
  inFlightIdentities.clear();
  nextToken = 1;
};
